import styled, { css } from 'styled-components';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { Typography } from '../../global/theme';
import { AlignCenter, FlexCenter, LineClamp } from '../../global/commonStyle';

interface Props {
  color?: string;
  applyTopSpacing?: boolean;
  isArchived?: boolean;
}

const Icon = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  &:hover {
    background-color: var(--background-muted);
    border-radius: 4px;
    svg {
      filter: brightness(var(--brightness-value2));
    }
  }

  ${mobile_xl(css`
    display: flex;
    margin-top: var(--space-0);
    padding: var(--space-6);
    &:hover {
      background-color: transparent;
    }
    svg {
      width: 20px;
      height: 20px;
    }
  `)}
`;

const ProjectContent = styled.div<Props>`
  border: 0.5px solid var(--border-primary);
  background-color: var(--background-primary);
  padding: var(--space-16) var(--space-16) var(--space-12);
  border-radius: 12px;
  cursor: ${({ isArchived }) => (isArchived ? 'default' : 'pointer')};
  transition: all var(--transition-normal);
  &:hover {
    box-shadow: ${({ isArchived }) => (isArchived ? 'unset' : 'var(--shadow-card-hover)')};
    ${Icon} {
      display: flex;
    }
  }
  ${mobile_xl(css`
    padding-block: var(--space-12);
    width: 100%;
    max-width: -webkit-fill-available;
    background-color: unset;
    border: unset;
    border-bottom: 0.5px solid var(--border-primary);
    border-radius: unset;
    :last-child {
      border-bottom: unset;
    }
    &:hover {
      box-shadow: none;
    }
  `)}
  ${(props) =>
    props.applyTopSpacing &&
    css`
      ${mobile_xl(css`
        padding-top: var(--space-16);
      `)}
    `}
`;

const ProjectItem = styled.div`
  width: 100%;
  max-width: 238px;
  ${mobile_xl(css`
    max-width: -webkit-fill-available;
    width: 100%;
  `)}
`;

const ProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  align-items: flex-start;
  ${mobile_xl(css`
    align-items: center;
    width: auto;
    max-width: 100%;
    margin: var(--space-0);
    border-radius: 12px;
  `)}
`;
const ProjectListTopSection = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  p {
    margin: var(--space-0);
  }
  ${mobile_xl(css`
    align-items: center;
    gap: var(--space-8);
  `)}
`;

const Document = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  ${mobile_xl(css`
    margin-top: var(--space-0);
  `)}
`;

const Title = styled.h1`
  ${Typography.body_lg_medium}
  color:var(--text-primary);
  margin: var(--space-0);
  ${LineClamp(1)}
  word-break: break-all;
  ${mobile_xl(css`
    font-weight: 600;
  `)}
`;

const Text = styled.p`
  ${Typography.body_md}
  color: var(--text-secondary);
  margin: var(--space-0);
  ${LineClamp(1)}
  word-break: break-all;
  min-height: 19px;
  ${mobile_xl(css`
    min-height: 0;
  `)}
`;

const Point = styled.div`
  position: relative;
  border: none;
  ${AlignCenter}
  ${mobile_xl(css`
    gap: var(--space-6);
  `)}
`;

const SmallDiv = styled.small<Props>`
  position: absolute;
  border-radius: 50%;
  background-color: ${(props: Props) => props.color};
  top: 5px;
  width: 10px;
  height: 10px;
  left: 0px;
`;

const ProgressStatus = styled.span`
  position: relative;
  ${Typography.body_sm}
  color: var(--text-white);
  ${mobile_xl(css`
    color: var(--text-primary);
    padding-left: var(--space-16);
  `)}
`;

const ProgressDiv = styled.div`
  display: flex;
  .progressBar {
    background-color: var(--neutral-secondary);
  }
`;

const AssignContent = styled.div`
  width: 100%;
  gap: var(--space-20);
  ${mobile_xl(css`
    padding-right: var(--space-10);
    max-width: 75px;
    width: max-content;
    ${AlignCenter}
    justify-content: flex-end;
  `)}
`;

const AssignGroup = styled.div`
  max-width: 140px;
  ul {
    display: flex;
    flex-wrap: wrap;
    list-style-type: none;
    padding: var(--space-0);
    margin: var(--space-0);
  }
  li {
    margin-right: -8px;
    position: relative;
    ${mobile_xl(css`
      margin-right: -10px;
    `)}
  }
  span {
    ${Typography.body_xs_semibold}
    color: var(--text-white);
  }
  .plus-icon {
    width: 22px;
    height: 22px;
    border-radius: 24px;
    ${FlexCenter}
    color: var(--text-primary);
    background: var(--background-muted);
    ${Typography.body_xs_semibold}
    border: 1px solid var(--background-primary);
    position: relative;
    top: 0;
    z-index: var(--z-index-base);
  }
  ${mobile_xl(css`
    max-width: unset;
    width: max-content;
    ul {
      justify-content: flex-end;
    }
    .avatar {
      width: 22px;
      height: 22px;
    }
    .plus-icon {
      width: 22px;
      height: 22px;
      padding-bottom: var(--space-0);
      border: 1px solid var(--background-primary);
    }
  `)}
`;
const Plus = styled.div`
  position: absolute;
  top: 5px;
`;
const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-12);
  height: 100%;
  ${mobile_xl(css`
    gap: var(--space-10);
  `)}
`;
const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  ${mobile_xl(css`
    flex-direction: row;
    padding-left: var(--space-48);
    justify-content: space-between;
    align-items: center;
  `)}
`;

const TitleSection = styled.div`
  width: 100%;
  display: flex;
  /* classname is given for avatar text */
  .project-title {
    span {
      ${Typography.body_xl_semibold}
    }
  }
  ${mobile_xl(css`
    width: unset;
    flex-direction: row;
    gap: var(--space-10);
    align-items: center;
    .project-title {
      padding-block: var(--space-4) var(--space-6);
    }
  `)}
`;

const TitleTopSection = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  ${mobile_xl(css`
    width: unset;
  `)}
`;

const Badge = styled.div`
  padding: var(--space-2) var(--space-4);
  background-color: var(--neutral-secondary);
  border-radius: var(--border-radius-4);
`;

const BadgeTitle = styled.div`
  margin: var(--space-0);
  ${Typography.body_sm}
  color: var(--text-primary);
`;

export {
  ProjectContent,
  ProjectItem,
  ProjectList,
  Document,
  Icon,
  Title,
  Text,
  Point,
  ProgressDiv,
  AssignContent,
  AssignGroup,
  Plus,
  Box,
  Bottom,
  ProjectListTopSection,
  TitleSection,
  TitleTopSection,
  SmallDiv,
  ProgressStatus,
  Badge,
  BadgeTitle
};
